import React from 'react';

import { FontAwesomeIcon, Text } from '@schoolinks/ui-components';

import { isEmpty, isNaN, isNumber } from 'lodash';
import queryString from 'query-string';

import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons/faCircleXmark';

import { MonthDayFullYearDashed } from 'constants/dateTimeFormats';
import { ERROR_STATUS_CODE_MAP } from 'core/axios/handleError';
import {
  COLLEGE_APPLICATIONS_CASEFILE,
  COLLEGE_APPLICATIONS_APP,
} from 'routes/constants';
import dayjs from 'services/dayjs';

import { ensureArray } from 'utils/arrays';
import { ensureObject } from 'utils/objects';
import { validEmail } from 'utils/validate';

import { ACCEPTANCE_LETTER_UPLOAD_REQUIREMENT_MAP } from 'modules/CollegeAppConfig/constants';
import {
  NON_CA_RELATED_FORM_ERROR,
  MAX_RECOMMENDATIONS_COUNT_EXCEEDED,
  RESPONSIBLE_PARTY_STUDENT,
  ACCEPTED_STATUSES,
} from 'modules/CollegeApplicationManager/constants';

import v from 'components/variables';

export const isZipCode = value => {
  if (!value) return undefined;
  if (value.length === 5) return undefined;
  if (value.length === 10 && value?.[5] === '-') return undefined;
  if (value.length === 7 && value?.[3] === ' ') return undefined;
  return 'Invalid Zip Code';
};

export const composeValidators =
  (...validators) =>
  value =>
    validators.reduce(
      (error, validator) => error || validator(value),
      undefined,
    );

export const required = value => {
  if (typeof value === 'string' && !value?.trim()) return 'Required';
  return value ? undefined : 'Required';
};

export const notPersonalEmail = (value, isPresetDestination) => {
  const personalEmailDomains = [
    'gmail.com',
    'yahoo.com',
    'outlook.com',
    'icloud.com',
    'aol.com',
    'sbcglobal.net',
    'att.net',
    'comcast.net',
    'live.com',
    'msn.com',
    'me.com',
    'cox.net',
    'bellsouth.net',
  ];
  const errorMessage =
    'Personal emails not allowed - this must be an official email at the college you are applying to.';

  if (isPresetDestination) {
    return undefined;
  }

  return personalEmailDomains.some(personalEmailDomain =>
    value.toLowerCase().includes(personalEmailDomain),
  )
    ? errorMessage
    : undefined;
};

export const statuses = [-1, 0, 1, 2, 3, 4, 5, 6];

export const getApplicationLink = (
  application,
  status = undefined,
  studentId = null,
  referrer = '',
  isEnrolling = false,
  params,
) => {
  const { uuid, heSchool } = { ...application };
  const { pk } = { ...heSchool };
  const baseEndpoint = studentId
    ? `${COLLEGE_APPLICATIONS_CASEFILE}/${studentId}/app/${pk}/${uuid}`
    : `${COLLEGE_APPLICATIONS_APP}/${pk}/${uuid}`;

  const parsed = {
    ...(statuses.includes(status) ? { presetStatus: status } : {}),
    ...(referrer ? { referrer } : {}),
    ...(isEnrolling ? { isEnrolling: true } : {}),
    ...params,
  };

  if (!isEmpty(parsed))
    return `${baseEndpoint}?${queryString.stringify(parsed)}`;
  return baseEndpoint;
};

export const getLink = link =>
  link.startsWith('http') ? link : `http://${link}`;

export const applicationStatuses = [
  {
    id: -1,
    title: 'Will Not Complete',
    color: '#BBB',
    icon: () => (
      <Text display="inline" variant='lead400'>
        ❌
      </Text>
    ),
  },
  {
    id: 0,
    title: 'In Progress',
    color: '#fab251',
    icon: () => (
      <Text display="inline" variant='lead400'>
        ✏️
      </Text>
    ),
  },
  {
    id: 1,
    title: 'Completed',
    color: '#8FB450',
    icon: () => (
      <Text display="inline" variant='lead400'>
        ✅
      </Text>
    ),
  },
];

export const resultStatuses = [
  {
    id: 1,
    title: 'Pending results',
    color: '#BBB',
    icon: () => (
      <Text display="inline" variant='lead400'>
        ❓
      </Text>
    ),
  },
  {
    id: 4,
    title: 'Accepted',
    color: '#8FB450',
    icon: () => (
      <Text display="inline" variant='lead400'>
        😃
      </Text>
    ),
  },
  {
    id: 6,
    title: 'Denied',
    color: '#EC6D6E',
    icon: () => (
      <Text display="inline" variant='lead400'>
        😕
      </Text>
    ),
  },
  {
    id: 2,
    title: 'Waitlisted',
    color: '#fab251',
    icon: () => (
      <Text display="inline" variant='lead400'>
        🕓
      </Text>
    ),
  },
  {
    id: 3,
    title: 'Deferred',
    color: '#fab251',
    icon: () => (
      <Text display="inline" variant='lead400'>
        ⌛
      </Text>
    ),
  },
  {
    id: 5,
    title: 'Withdrawn',
    color: '#bbb',
    icon: () => (
      <Text display="inline" variant='lead400'>
        📤
      </Text>
    ),
  },
];

export const allStatuses = {
  willNotComplete: -1,
  inProgress: 0,
  completed: 1,
  waitlisted: 2,
  deferred: 3,
  accepted: 4,
  withdrawn: 5,
  rejected: 6,
};

export const allNumericalStatuses = {
  '-1': 'Will Not Complete',
  0: 'In Progress',
  1: 'Completed & Pending Results',
  2: 'Waitlisted',
  3: 'Deferred',
  4: 'Accepted',
  5: 'Withdrawn',
  6: 'Rejected',
};

export const customRequirementExtraPayloads = {
  hs: {
    type: 'ATTACHMENT',
    responsibleParty: 'k12_admin',
    stage: 'SUBMISSION',
  },
  student: {
    type: 'TO_DO',
    responsibleParty: 'student',
    stage: 'SUBMISSION',
  },
};

export const recommendationEventStatusMap = {
  Added: 'awaiting',
  Requested: 'awaiting',
  Restored: 'awaiting',
  NotSubmitted: 'awaiting',
  Uploaded: 'submitted',
  Sent: 'submitted',
  Viewed: 'submitted',
  Declined: 'declined',
  Removed: 'awaiting',
  Waived: 'waived',
};

export const recommendationStatusMap = {
  awaiting: {
    icon: <FontAwesomeIcon color="orangeDark" icon={faCircle} />,
    description: 'Request Pending',
    color: v.orangeDark,
  },
  submitted: {
    icon: <FontAwesomeIcon color="green" icon={faCircleCheck} />,
    description: 'Request Fulfilled',
    color: v.green,
  },
  declined: {
    icon: <FontAwesomeIcon color="red" icon={faCircleXmark} />,
    description: 'Declined',
    color: v.red,
  },
  waived: {
    icon: <FontAwesomeIcon color="green" icon={faCircleCheck} />,
    description: 'Waived',
    color: v.green,
  },
};

// eslint-disable-next-line complexity
export const validateApplicationDocDestination = (
  values,
  {
    isK12Admin,
    inNetwork,
    isStudentsEmail,
    isInternationalSchool,
    isPresetDestination,
  },
) => {
  if (isK12Admin) {
    return {};
  }
  const errors = { destination: {} };
  if (!inNetwork && values.methodPk !== 1 && isEmpty(values.destination)) {
    errors.destination = 'Required';
  }

  if (values.destination?.type === 'email') {
    errors.destination.receiverName = required(
      values.destination?.receiverName,
    );
    errors.destination.receiverAttn = required(
      values.destination?.receiverAttn,
    );
    errors.destination.receiverEmail = composeValidators(
      required,
      validEmail,
      value => notPersonalEmail(value, isPresetDestination),
      () =>
        isStudentsEmail
          ? 'Personal emails not allowed - this must be an official email at the college you are applying to.'
          : undefined,
    )(values.destination?.receiverEmail);
  }
  if (values.destination?.type === 'mail') {
    errors.destination.receiverName = required(
      values.destination?.receiverName,
    );
    errors.destination.receiverAttn = required(
      values.destination?.receiverAttn,
    );
    errors.destination.receiverAddress1 = required(
      values.destination?.receiverAddress1,
    );
    if (!isInternationalSchool) {
      errors.destination.receiverCity = required(
        values.destination?.receiverCity,
      );
      errors.destination.receiverState = required(
        values.destination?.receiverState,
      );
      errors.destination.receiverZipCode = composeValidators(
        required,
        isZipCode,
      )(values.destination?.receiverZipCode);
    }
  }
  if (values.enrollmentDueDate) {
    errors.enrollmentDueDate = !dayjs(
      values.enrollmentDueDate,
      MonthDayFullYearDashed,
      true,
    ).isValid()
      ? 'Please enter date in correct format, e.g:- Jul 21'
      : undefined;
  }

  return errors;
};

export const validateApplicationDetails = (
  values,
  {
    inNetwork,
    hideDeadline,
    hideDocDestination,
    isStudentsEmail,
    isDemo,
    isInternationalSchool,
    isPresetDestination,
  },
) => {
  let errors = {
    destination: {},
  };

  if (isDemo && values.methodPk === allStatuses.completed) {
    return errors;
  }

  errors.type = required(values.type);
  if (!hideDeadline)
    errors.submissionDueDate = composeValidators(required, val => {
      if (!val) return undefined;
      if (!dayjs(val).isValid()) return 'Invalid date';
      return undefined;
    })(values.submissionDueDate);

  if (!hideDocDestination) {
    errors = {
      ...errors,
      ...validateApplicationDocDestination(values, {
        inNetwork,
        isStudentsEmail,
        isInternationalSchool,
        isPresetDestination,
      }),
    };
  }

  return errors;
};

export const getRestoreRecommendationErrorMessage = (error, schoolSpecific) => {
  const errorCode = ensureArray(error.response?.code);
  let message = 'There was a problem restoring the recommendation.';

  if (errorCode.includes(MAX_RECOMMENDATIONS_COUNT_EXCEEDED)) {
    message = schoolSpecific
      ? 'The student has met the recommendation limit for this application, so this requirement cannot be restored.'
      : 'The student has met the recommendation limit on their applications so this requirement cannot be restored.';
  }

  return message;
};

export const getRequirementsForDropdown = ({
  presetRequirements,
  schoolApplication,
  responsiblePartyId,
  schoolContext,
}) => {
  const presetApplicationRequirements = ensureArray(
    schoolContext?.presetApplication?.presetApplicationRequirements,
  );
  const appRequirements = ensureArray(
    responsiblePartyId === RESPONSIBLE_PARTY_STUDENT
      ? schoolApplication?.studentRequirements
      : schoolApplication?.highSchoolRequirements,
  );
  const suggestedRequirements = [];
  ensureArray(presetRequirements).forEach(presetRequirement => {
    if (
      !appRequirements
        .map(r => r.presetRequirementId)
        .includes(presetRequirement.id)
    ) {
      const presetApplicationRequirement = presetApplicationRequirements.find(
        ({ presetRequirementId }) =>
          presetRequirementId === presetRequirement.id,
      );
      const prefillData = {
        name: presetRequirement.name,
        description:
          presetApplicationRequirement?.description ??
          presetRequirement?.description,
        required:
          presetApplicationRequirement?.required ?? presetRequirement?.required,
      };
      suggestedRequirements.push({ ...presetRequirement, ...prefillData });
    }
  });
  return suggestedRequirements;
};

export const isAcceptanceLetterUploadRequired = ({
  isEnrolling,
  schoolConfig,
  status = null,
}) => {
  const { acceptanceLetterUploadRequirement } = ensureObject(schoolConfig);
  const checkStatus = isNumber(status)
    ? ACCEPTED_STATUSES.includes(status)
    : true;

  if (
    acceptanceLetterUploadRequirement ===
      ACCEPTANCE_LETTER_UPLOAD_REQUIREMENT_MAP.ALL_COLLEGES &&
    checkStatus
  ) {
    return true;
  }

  if (
    acceptanceLetterUploadRequirement ===
    ACCEPTANCE_LETTER_UPLOAD_REQUIREMENT_MAP.ENROLLED_COLLEGES
  ) {
    return isEnrolling;
  }

  return false;
};

export const onCreateNewApplication = async ({
  createApplication,
  studentId,
  schoolId,
}) =>
  createApplication.mutateAsync({
    payload: {
      ...(studentId ? { student_pk: studentId } : {}),
      heSchoolPk: schoolId,
      status: allStatuses.completed, // hardcode complete status when creation instead of accepted
      isActive: false, // this will be marked as true on the BE side when the acceptance is confirmed
    },
  });

export const getRecommendationApplicationUuids = recommendation =>
  ensureArray(recommendation?.requirements).map(
    requirement => requirement?.application?.uuid,
  );

export const convertToQueryStage = stage => {
  const converted = stage.toUpperCase();
  if (converted === 'MIDYEAR') {
    return 'MID_YEAR';
  }
  return converted;
};

export const sleep = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

/**
 * @param {ErrorResponse} error -> Error received from BE that can be CA Error or BE Error
 * @param {Object<{formName: {fields, choices}}>} forms -> Overall form choices & fields
 * @returns {Array<{message, form, isCommonApp, field}>}
 */
export const getErrorsFromChoices = ({ error = {}, forms = {} }) => {
  const errors = [];
  const { response, statusCode } = error;

  // This is for 500 Server error, will be removed after BE Changes
  if (statusCode === ERROR_STATUS_CODE_MAP.INTERNAL_SERVER_ERROR) {
    errors.push({
      message: response.message || response,
      form: NON_CA_RELATED_FORM_ERROR,
      isCommonApp: false,
      field: null,
    });

    return errors;
  }

  // Map the error response array returned from submit error
  ensureArray(response?.details)
    .map(ensureObject)
    .forEach(({ message, questionId }) => {
      const { message: id } = ensureObject(questionId);

      // Check form has question related blocker
      // isNan(Number(id)) will be true if id is a number wrapped in string ex: "1234"
      if (isNaN(Number(id))) {
        // If non question related, just push message as it is and show it as it is in given format
        errors.push({
          message: ensureObject(message).message,
          form: NON_CA_RELATED_FORM_ERROR,
          isCommonApp: true,
          field: null,
        });
      } else {
        // Go through forms to receive fields & form name
        Object.entries(forms).forEach(([key, value]) => {
          // Get the field that blocks the submitting
          const queriedField = value.fields.find(
            field => field.questionId === Number(id),
          );

          // Pass it in to the errors array and in a correct format
          if (queriedField) {
            errors.push({
              message: ensureObject(message).message,
              field: queriedField,
              form: key,
              isCommonApp: true,
            });
          }
        });
      }
    });

  return errors;
};
